/** Parsed `data-feedback-*` attributes off the widget's own `<script>` tag. */
export interface ScriptConfig {
  key?: string;
  apiBase?: string;
  redactSelectors?: string[];
  captureConsole?: boolean;
}

/**
 * Locates the widget's own `<script>` tag and parses its `data-feedback-*` attributes.
 *
 * Resolution order:
 * 1. `document.currentScript` — set by the browser while a classic `<script src="...">` is
 *    synchronously executing. This is how the widget ships (a plain IIFE tag), so in
 *    production this is almost always what resolves the script element.
 * 2. A fallback lookup for `script[data-feedback-key]` — covers environments where
 *    `document.currentScript` isn't populated (e.g. the script already finished executing,
 *    or a test harness inserted the tag without "running" it) but a widget script element
 *    with the expected attribute is present in the document.
 *
 * Returns `null` when no candidate script element is found at all — e.g. this module was
 * imported in a test/SSR/Node context with no script tag present. Never throws: any DOM
 * access failure (locked-down host, missing `document`) is swallowed and treated as "no
 * config found" so callers can safely call this unconditionally.
 */
export function readScriptConfig(): ScriptConfig | null {
  try {
    const scriptEl =
      (document.currentScript as HTMLScriptElement | null) ??
      document.querySelector<HTMLScriptElement>('script[data-feedback-key]');

    if (!scriptEl) return null;

    const key = scriptEl.dataset.feedbackKey || undefined;
    const apiBase = scriptEl.dataset.feedbackApi || undefined;

    const redactAttr = scriptEl.dataset.feedbackRedact;
    const redactSelectors = redactAttr
      ? redactAttr
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;

    // Presence-as-boolean: `data-feedback-capture-console` (empty value) or
    // `data-feedback-capture-console="true"` both mean "on"; an explicit "false" means
    // "off"; the attribute being absent entirely leaves this `undefined` (caller default).
    const captureConsoleAttr = scriptEl.dataset.feedbackCaptureConsole;
    const captureConsole = captureConsoleAttr === undefined ? undefined : captureConsoleAttr !== 'false';

    return { key, apiBase, redactSelectors, captureConsole };
  } catch {
    return null;
  }
}
